/**
 * The round-transition explosion, as arithmetic. SPEC.md §4.4, §19, §20.4.
 *
 * A hand that has ended used to announce itself as a line of text — "HAND 4
 * closed · +38 / -38" — in the same snackbar lane an entry's UNDO arrives in.
 * It is now a burst of playing cards over the table, and the reason is what the
 * line was competing with: the hand it named had *already* been replaced by the
 * next one, in the same transaction, before the notice could be read (§4.3). A
 * player who looked up read a sentence about a hand that was no longer on
 * screen, in the lane they had learned means "there is something to press". The
 * cards say the one thing that is still true a second later — that the table has
 * moved on — and say it without a word to read or a control to avoid.
 *
 * Every number below is a port of the reference composition, and the shape of
 * the port is deliberate: the geometry lives here as data, and the component
 * hands it to `Element.animate`. Nothing in this file touches the DOM, so the
 * claim the brief actually makes — that all forty-two cards fly independently —
 * is a thing a unit test can read rather than a thing a screenshot has to
 * suggest. §42.2
 *
 * **The randomness is seeded, and that is not an optimisation.** A composition
 * re-rolled per hand is one that is beautiful on the hand you looked at and
 * lopsided on the next, with no way to tell which you shipped. Seeded, the burst
 * is chaotic *and* the same chaos every time — so it can be tuned once, asserted
 * on, and trusted at the table.
 */

/** How many cards are in the air. §4.4 */
export const JACKPOT_CARD_COUNT = 42;

/** Separate sparkles, for the slot-machine sheen over the top of the cards. */
export const JACKPOT_GLINT_COUNT = 9;

/**
 * The whole primary effect, and the budget everything else is fitted into.
 *
 * Half a second is short enough that it is over before a player's next tap and
 * long enough to read as an event rather than a flicker. The cards' own launches
 * are staggered inside it rather than after it, so the last card to leave still
 * lands on the same final frame as the first. §20.4
 */
export const JACKPOT_DURATION_MS = 500;

/**
 * When the layer is safe to put away again.
 *
 * Not the same as the duration: a card may launch up to `JACKPOT_MAX_DELAY_MS`
 * late, and a glint runs on its own clock past both. This is the last frame of
 * the slowest of them, so the teardown never cuts one off mid-flight.
 */
export const JACKPOT_MAX_DELAY_MS = 54;
export const JACKPOT_TEARDOWN_MS = 700;

/** The reduced-motion substitute: one soft flash, and no cards at all. §19 */
export const JACKPOT_REDUCED_DURATION_MS = 320;

/**
 * The seeded generator, verbatim from the reference. §42.2
 *
 * Mulberry32: one 32-bit word of state, avalanched into a float in [0, 1). It is
 * here rather than `Math.random` because the composition has to be the same
 * composition every time — see the note at the top of the file — and it is this
 * one rather than something shorter because a weaker mixer puts visible
 * structure into a fan of forty-two angles, which is exactly the "uniform
 * vortex" the trajectories are shaped to avoid.
 */
export function mulberry32(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface JackpotCardFace {
  /** The pip drawn large in the middle, and the corner mark. */
  suit: string;
  rank: string;
  /** Hearts and diamonds are red; spades and clubs are the card's ink colour. */
  red: boolean;
}

const SUITS: readonly JackpotCardFace[] = [
  { suit: '♠', rank: '', red: false },
  { suit: '♥', rank: '', red: true },
  { suit: '♦', rank: '', red: true },
  { suit: '♣', rank: '', red: false },
];

const RANKS = ['A', 'K', 'Q', 'J', '10', '9', '8', '7', '6', '5', '4', '3', '2'] as const;

/**
 * The forty-two faces, in a fixed order. §4.4
 *
 * Real ranks and real suits rather than coloured rectangles, because a rectangle
 * spinning past at 1,100 degrees a second is confetti and a *card* is the game
 * being played. The rank walks by seven so consecutive cards never share one —
 * thirteen and seven are coprime, so the sequence visits every rank before it
 * repeats — while the suit cycles by four, which is what keeps red and black
 * evenly mixed through the fan.
 */
export function jackpotCardFaces(): JackpotCardFace[] {
  return Array.from({ length: JACKPOT_CARD_COUNT }, (_, index) => {
    const suit = SUITS[index % SUITS.length] ?? SUITS[0]!;
    return { ...suit, rank: RANKS[(index * 7 + 3) % RANKS.length] ?? 'A' };
  });
}

/** The box the burst is fitted to — the layer's own measured size, in px. */
export interface JackpotBounds {
  width: number;
  height: number;
}

export interface JackpotFlight {
  delayMs: number;
  durationMs: number;
  keyframes: Keyframe[];
  easing: string;
}

/**
 * How far a card may travel, and it is capped rather than proportional. §18.1
 *
 * The fan is sized to the screen up to a point and then stops growing: past
 * about 530 px of width the cards would spread far enough apart to read as five
 * separate little animations rather than one burst. The vertical cap is tighter
 * than the horizontal one for the same reason a phone is tall — a burst that
 * used the whole height of a 932 px screen would take most of a second to cross
 * it at any speed that still looks thrown.
 */
function radii(bounds: JackpotBounds): { x: number; y: number } {
  return {
    x: Math.min(bounds.width * 0.48, 255),
    y: Math.min(bounds.height * 0.47, 150),
  };
}

/**
 * One card's flight. §4.4, §20.4
 *
 * Nineteen independent draws per card, off a seed that is the card's own — so
 * every one of the properties the brief asks to be independent genuinely is:
 * launch angle, distance, three intermediate control points, bend direction,
 * spin direction and rate, both flip axes, launch delay, peak scale and final
 * scale. Two cards are never on the same path and never on a mirror of one.
 *
 * The route is a four-segment polyline through `p1`, `p2` and `p3` rather than a
 * straight line to `end`, and the bends alternate in sign: a fan of forty-two
 * cards all curving the same way is a vortex, which reads as a mechanism rather
 * than as a throw. The `- 10`, `- 16` and `- (18 + …)` terms are the upward bias
 * — cards leave the impact slightly against gravity, the way a thrown deck does,
 * and only the last third of the flight carries them back down.
 *
 * The whole thing is transform and opacity, and that is load-bearing rather than
 * tidy: forty-two elements changing anything else would be forty-two layouts a
 * frame, over a keypad somebody is mid-tap on. §20.4, §18.4
 */
export function jackpotCardFlight(index: number, bounds: JackpotBounds): JackpotFlight {
  const rand = mulberry32(8701 + index * 97);
  const { x: radiusX, y: radiusY } = radii(bounds);

  // Broad radial coverage, nudged off the perfect fan so it is a throw rather
  // than a clock face.
  const base = (Math.PI * 2 * index) / JACKPOT_CARD_COUNT + (rand() - 0.5) * 0.38;
  const distance = 0.62 + rand() * 0.48;
  const endX = Math.cos(base) * radiusX * distance;
  const endY = Math.sin(base) * radiusY * distance - (18 + rand() * 18);

  // Every third card bends against its neighbours, and then a coin toss on top
  // of that: two rules rather than one, because a single alternation of forty-two
  // is itself a pattern the eye finds.
  const bendSign = (index % 3 === 0 ? -1 : 1) * (rand() > 0.5 ? 1 : -1);
  const tangentX = -Math.sin(base);
  const tangentY = Math.cos(base);
  const bend1 = (20 + rand() * 52) * bendSign;
  const bend2 = (12 + rand() * 48) * -bendSign;

  const p1x = endX * 0.18 + tangentX * bend1;
  const p1y = endY * 0.12 + tangentY * bend1 - 10 - rand() * 15;
  const p2x = endX * 0.52 + tangentX * bend2;
  const p2y = endY * 0.43 + tangentY * bend2 - 16 - rand() * 22;
  const p3x = endX * 0.82 + tangentX * (bend1 * 0.25);
  const p3y = endY * 0.78 + tangentY * (bend1 * 0.18) + rand() * 16;

  const startRot = -30 + rand() * 60;
  const spin = (rand() > 0.5 ? 1 : -1) * (430 + rand() * 720);
  const flipX = (rand() > 0.5 ? 1 : -1) * (180 + rand() * 540);
  const flipY = (rand() > 0.5 ? 1 : -1) * (180 + rand() * 540);
  const delayMs = rand() * JACKPOT_MAX_DELAY_MS;
  const peakScale = 0.92 + rand() * 0.42;
  const endScale = 0.52 + rand() * 0.28;

  /**
   * A pose, and the spin and the flips take their own fractions of it.
   *
   * They are the same number at every keyframe but the first, where the card has
   * turned 16% of its spin and 15% of each flip — a quarter of a degree apart on
   * a card that is fifteen pixels wide. It is carried across because the whole
   * point of porting a composition rather than reinventing one is that the next
   * person can diff it against the reference and find nothing.
   */
  const at = (
    x: number,
    y: number,
    turn: number,
    scale: number,
    flipTurn = turn,
  ): string =>
    `translate(-50%,-50%) translate(${String(x)}px,${String(y)}px) rotate(${String(
      startRot + spin * turn,
    )}deg) rotateX(${String(flipX * flipTurn)}deg) rotateY(${String(
      flipY * flipTurn,
    )}deg) scale(${String(scale)})`;

  return {
    delayMs,
    durationMs: JACKPOT_DURATION_MS,
    // Linear across the whole flight: the shape of the motion is in the control
    // points, and a curve on top of them would ease *between* bends and flatten
    // the throw into a drift.
    easing: 'linear',
    keyframes: [
      {
        offset: 0,
        opacity: 0,
        // Out of nothing and already bright — the card is lit by the impact it
        // is leaving rather than fading in from the background.
        transform: at(0, 0, 0, 0.08),
        filter: 'brightness(1.8) drop-shadow(0 0 4px rgba(255,255,255,.9))',
      },
      {
        offset: 0.19,
        opacity: 1,
        transform: at(p1x, p1y, 0.16, peakScale, 0.15),
        filter: 'brightness(1.35) drop-shadow(0 4px 7px rgba(0,0,0,.34))',
      },
      {
        offset: 0.45,
        opacity: 1,
        transform: at(p2x, p2y, 0.48, peakScale * 0.94),
        filter: 'brightness(1.08) drop-shadow(0 5px 8px rgba(0,0,0,.38))',
      },
      {
        offset: 0.76,
        opacity: 0.88,
        transform: at(p3x, p3y, 0.78, endScale + 0.14),
        filter: 'brightness(1) drop-shadow(0 4px 7px rgba(0,0,0,.3))',
      },
      {
        offset: 1,
        // Every card is gone by the last frame, which is what lets the layer be
        // put away without anything to fade out afterwards.
        opacity: 0,
        transform: at(endX, endY, 1, endScale),
        filter: 'brightness(.95) drop-shadow(0 2px 4px rgba(0,0,0,.2))',
      },
    ],
  };
}

/**
 * One sparkle. §4.4
 *
 * Nine of them, each on its own clock — a shorter run than the cards and a later
 * start, so they read as light catching the cards rather than as more cards. The
 * spread is a fraction of the layer rather than of the fan, which is what puts
 * them *around* the burst instead of inside it.
 */
export function jackpotGlintFlight(index: number, bounds: JackpotBounds): JackpotFlight {
  const rand = mulberry32(241 + index * 31);
  const x = (rand() - 0.5) * bounds.width * 0.72;
  const y = (rand() - 0.5) * bounds.height * 0.52;
  const midScale = 0.7 + rand() * 0.65;
  const midTurn = rand() * 90;
  const endTurn = 90 + rand() * 100;
  const durationMs = 360 + rand() * 100;
  const delayMs = 70 + rand() * 120;

  return {
    delayMs,
    durationMs,
    easing: 'cubic-bezier(.12,.72,.2,1)',
    keyframes: [
      { offset: 0, opacity: 0, transform: 'translate(-50%,-50%) translate(0,0) scale(.15) rotate(0deg)' },
      {
        offset: 0.45,
        opacity: 1,
        transform: `translate(-50%,-50%) translate(${String(x * 0.72)}px,${String(
          y * 0.72,
        )}px) scale(${String(midScale)}) rotate(${String(midTurn)}deg)`,
      },
      {
        offset: 1,
        opacity: 0,
        transform: `translate(-50%,-50%) translate(${String(x)}px,${String(y)}px) scale(.12) rotate(${String(
          endTurn,
        )}deg)`,
      },
    ],
  };
}

/**
 * The four pieces of the impact itself, in the order they are drawn. §4.4
 *
 * They are one object rather than four calls because their timings are a single
 * shape: the core flashes and is over before the halo has finished opening, the
 * sunburst arrives *after* the flash rather than with it, and the wash peaks
 * first and is the first thing gone. Split across four call sites, that shape
 * would be four numbers nobody keeps in step.
 */
export const JACKPOT_IMPACT: Record<'core' | 'halo' | 'sun' | 'wash', Omit<JackpotFlight, 'delayMs'>> =
  {
    core: {
      durationMs: JACKPOT_DURATION_MS,
      easing: 'cubic-bezier(.12,.72,.2,1)',
      keyframes: [
        { offset: 0, opacity: 0, transform: 'translate(-50%,-50%) scale(.15)' },
        { offset: 0.13, opacity: 1, transform: 'translate(-50%,-50%) scale(1)' },
        { offset: 0.48, opacity: 0.72, transform: 'translate(-50%,-50%) scale(3.3)' },
        { offset: 1, opacity: 0, transform: 'translate(-50%,-50%) scale(5.8)' },
      ],
    },
    halo: {
      durationMs: JACKPOT_DURATION_MS,
      easing: 'cubic-bezier(.08,.72,.18,1)',
      keyframes: [
        { offset: 0, opacity: 0, transform: 'translate(-50%,-50%) scale(.2)' },
        { offset: 0.14, opacity: 1, transform: 'translate(-50%,-50%) scale(.7)' },
        { offset: 0.55, opacity: 0.8, transform: 'translate(-50%,-50%) scale(3.5)' },
        { offset: 1, opacity: 0, transform: 'translate(-50%,-50%) scale(7.8) rotate(70deg)' },
      ],
    },
    sun: {
      durationMs: JACKPOT_DURATION_MS,
      easing: 'cubic-bezier(.1,.75,.2,1)',
      keyframes: [
        { offset: 0, opacity: 0, transform: 'translate(-50%,-50%) scale(.12) rotate(-18deg)' },
        { offset: 0.18, opacity: 0.95, transform: 'translate(-50%,-50%) scale(.62) rotate(2deg)' },
        { offset: 0.62, opacity: 0.55, transform: 'translate(-50%,-50%) scale(1.2) rotate(30deg)' },
        { offset: 1, opacity: 0, transform: 'translate(-50%,-50%) scale(1.72) rotate(58deg)' },
      ],
    },
    wash: {
      durationMs: JACKPOT_DURATION_MS,
      easing: 'ease-out',
      keyframes: [
        { offset: 0, opacity: 0 },
        { offset: 0.12, opacity: 1 },
        { offset: 0.44, opacity: 0.42 },
        { offset: 1, opacity: 0 },
      ],
    },
  };

/**
 * What the table feels rather than sees. §4.4, §20.4
 *
 * A player unit dips, rises past where it started and settles — three quarters
 * of a bounce, five pixels at its widest, and over in the same half second as
 * the cards. It is applied per unit with a few milliseconds between them so the
 * roster reads as one surface being struck rather than as everything jumping at
 * once, and it is transform-only for the same reason the cards are: nothing on
 * this screen may reflow while a hand is being reported. §18.4
 *
 * It is deliberately this small. The brief that asked for the burst asked for
 * the response to it to be restrained, and a roster that shook would put the
 * scores — the only ones on the screen — out of focus at exactly the moment one
 * of them changed.
 */
export const JACKPOT_IMPACT_BOUNCE: Keyframe[] = [
  { offset: 0, transform: 'translateY(0) scale(1)' },
  { offset: 0.16, transform: 'translateY(3px) scale(.985)' },
  { offset: 0.38, transform: 'translateY(-5px) scale(1.025)' },
  { offset: 0.65, transform: 'translateY(1px) scale(.997)' },
  { offset: 1, transform: 'translateY(0) scale(1)' },
];

export const JACKPOT_BOUNCE_EASING = 'cubic-bezier(.16,.82,.22,1)';
/** Between one unit and the next, so the strike travels across the roster. */
export const JACKPOT_BOUNCE_STAGGER_MS = 9;
